import React, { useState } from "react";

import { Draggable } from "react-beautiful-dnd";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import SocialAccountAddOrEdit from "./socialMedia/SocialAccountAddOrEdit";
import {
  Button,
  DragAndDropWrapper,
  DragHandle,
  Icon,
  ListItem,
} from "../../components";
import { Flex } from "../../components/Container";
import {
  useCreateIndividualQuery,
  useDeleteIndividualQuery,
  useProfileSectionQuery,
  useUpdateIndividualProfileQuery,
} from "../../features/individualProfile/queries";
import { reorderItems } from "../../utils";
import Card from "../individualProfile/Card";

const isFullURL = url => {
  const pattern = new RegExp(
    /((?:https?:\/\/|www\.)(?:[-a-z0-9]+\.)*[-a-z0-9]+.*)/,
    "i",
  );

  return pattern.test(url);
};

const generateLink = socialMediaItem => {
  const { field_type } = socialMediaItem;
  return isFullURL(socialMediaItem?.value || "")
    ? socialMediaItem?.value
    : `${field_type?.base_value || ""}${socialMediaItem?.value}`;
};

const EditSocialNetworks = () => {
  const { profileHandle } = useParams();
  const { addToast } = useToasts();
  const history = useHistory();
  const location = useLocation();
  const { socialMediaItems } = location.state;

  const [navigationState, setNavigationState] = useState(() => {
    if (socialMediaItems.length) {
      return { action: null, payload: null };
    }
    return { action: "ADD", payload: null };
  });
  const socialAccountsList = socialMediaItems.map(item => {
    return {
      base_value: item.field_type.base_value,
      icon: item.field_type.icon,
      label: item.field_type.title,
      value: item.field_type.id,
      value_placeholder: item.field_type.value_placeholder,
      url: generateLink(item),
      id: item.id,
      order: item.order,
      walkthrough: item.field_type?.walkthrough,
    };
  });

  const sections = useProfileSectionQuery();

  const socialAccounts = sections?.sections_definition
    ?.find(section => section.id === "social_media")
    ?.field_types.map(({ id, title, ...rest }) => ({
      label: title,
      value: id,
      ...rest,
    }));

  const { mutate: createField, status: createStatus } =
    useCreateIndividualQuery({
      showToasts: true,
      createType: "field",
      onSuccess: () => {
        addToast("Success", {
          appearance: "success",
        });
      },
    });

  const { mutate: updateSocialAccounts, status: updateStatus } =
    useUpdateIndividualProfileQuery({
      showToasts: true,
      updateType: "field",
      successMsg: "Social links updated successfully",
    });

  const { mutate: deleteIndividual, status: deleteStatus } =
    useDeleteIndividualQuery({
      type: "generic",
      onSuccess: () => {
        addToast("Link Removed Successfully", {
          appearance: "success",
        });
      },
    });

  const isLoading =
    updateStatus === "loading" ||
    createStatus === "loading" ||
    deleteStatus === "loading";

  const handleSubmit = async values => {
    const { socialAccount } = values;
    const postValues = {
      baseValue: socialAccount?.base_value,
      field_type_id: socialAccount?.value,
      icon: socialAccount.icon,
      order: 0,
      value: values?.url,
      id: socialAccount?.id || "",
    };

    if (navigationState.payload) {
      const data = await updateSocialAccounts(
        {
          handle: profileHandle,
          profileData: [postValues],
        },
        {
          onError: () => {
            addToast(
              "Failed to update account details, please contact support for assistance.",
              {
                appearance: "error",
              },
            );
          },
        },
      );
      const { fields } = data;
      const updateIndex = socialMediaItems.findIndex(
        item => item.id === fields[0].id,
      );
      socialMediaItems[updateIndex] = fields[0];
      updateSocialMediaItems(socialMediaItems);
      setNavigationState({ action: null, payload: null });
      return;
    }

    const data = await createField(
      {
        handle: profileHandle,
        profileData: [postValues],
      },
      {
        onError: () => {
          addToast(
            "Failed to Create Field: please contact support for assistance.",
            {
              appearance: "error",
            },
          );
        },
      },
    );
    const { fields } = data;
    const updatedSocialAccounts = [...socialMediaItems, ...fields];
    updateSocialMediaItems(updatedSocialAccounts);
    setNavigationState({ action: null, payload: null });
  };
  const handleDelete = socialAccountId => {
    deleteIndividual(
      {
        handle: profileHandle,
        category: "field",
        id: socialAccountId,
      },
      {
        onSuccess: () => {
          const updatedSocialAccounts = socialMediaItems.filter(
            account => account.id !== socialAccountId,
          );
          updateSocialMediaItems(updatedSocialAccounts);
        },
      },
    );
  };

  const handleDragEnd = async result => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const reorderedSocialMediaItems = reorderItems(
      socialMediaItems,
      result.source.index,
      result.destination.index,
    );
    const updatedSocialMediaItems = reorderedSocialMediaItems.map(
      socialAccount => ({
        baseValue: socialAccount?.field_type.base_value,
        field_type_id: socialAccount?.field_type.id,
        icon: socialAccount?.field_type.icon,
        order: socialAccount?.order || 0,
        value: generateLink(socialAccount),
        id: socialAccount?.id || "",
      }),
    );

    await updateSocialAccounts(
      {
        handle: profileHandle,
        profileData: updatedSocialMediaItems,
      },
      {
        onError: () => {
          addToast(
            "Failed to update social accounts: please contact support for assistance.",
            {
              appearance: "error",
            },
          );
        },
      },
    );
    updateSocialMediaItems(reorderedSocialMediaItems);
  };

  const updateSocialMediaItems = items => {
    const state = { socialMediaItems: items };
    history.replace({ ...history.location, state });
  };

  return (
    <>
      <Card
        helmetTitle={`OVOU | ${
          socialMediaItems.length ? "Edit" : "Add"
        } Social media`}
        pageTitle={`${socialMediaItems.length ? "Edit" : "Add"} Social media`}
        label="Here you can add or edit social profiles to OVOU profile"
        showBorder={false}
      >
        {(navigationState.action === "ADD" ||
          navigationState.action === "EDIT") && (
          <>
            <SocialAccountAddOrEdit
              socialAccountOptions={socialAccounts}
              socialAccountItem={navigationState.payload}
              handleSubmit={handleSubmit}
              status={isLoading && "loading"}
            ></SocialAccountAddOrEdit>
          </>
        )}
        {!navigationState.action && (
          <DragAndDropWrapper
            onDragEnd={handleDragEnd}
            droppableId="socialAccountsList"
          >
            {socialAccountsList.map((socialAccount, i) => (
              <Draggable
                draggableId={socialAccount?.id?.toString()}
                key={socialAccount?.id}
                index={i}
              >
                {(provided, snapshot) => (
                  <Flex
                    width="100%"
                    {...provided.draggableProps}
                    ref={provided.innerRef}
                    isDragging={snapshot.isDragging}
                    {...provided.dragHandleProps}
                  >
                    <div className="mt-5">
                      <DragHandle />
                    </div>
                    <ListItem
                      key={socialAccount.id}
                      variant="link"
                      image={socialAccount?.icon}
                      link={socialAccount?.url}
                      label={socialAccount?.label}
                      showBorder={i + 1 !== socialMediaItems.length}
                      isEditable={true}
                      onDelete={() => handleDelete(socialAccount.id)}
                      onEdit={() =>
                        setNavigationState({
                          action: "EDIT",
                          payload: socialAccount,
                        })
                      }
                    />
                  </Flex>
                )}
              </Draggable>
            ))}
          </DragAndDropWrapper>
        )}
        {!navigationState.action && (
          <div className="sm:grid sm:grid-cols-2 sm:gap-4 sm:items-center mt-6 sm:mt-5 md:mt-12">
            <div className="col-start-1">
              <Button
                size="small"
                variant="white"
                block
                onClick={() =>
                  setNavigationState({ action: "ADD", payload: null })
                }
              >
                <Icon name="plusCircle" className="mr-2 text-current" />
                Add Social Account
              </Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
};

export default EditSocialNetworks;
